/**
 * What the search is asking for, and what may join it while it is open.
 *
 * Two rules live here rather than in the markup, because both are easy to get
 * subtly wrong and neither is visible by looking at the page.
 *
 * The query carries only what was asked, and a date range carries the
 * operator's own offset. Both live in `searchQuery.ts` now, because the message
 * log needs them too.
 *
 * WHAT IS LEFT HERE IS THE ONE RULE ONLY THIS PAGE HAS. A LIST MEANS WHAT ITS
 * FILTER SAYS. The live stream carries every
 * decision the service makes, so anything narrowing the list has to narrow the
 * arrivals too. Watching refusals at one charge point would otherwise fill
 * quietly with approvals from everywhere, and the reader has no way to tell
 * that the list stopped answering the question on screen. The date range is
 * the same rule read once more: a range that ends in the past cannot have new
 * members, so live rows do not belong in it at all.
 */

import type { DecisionSummary } from '../api'
import { asInstant, searchQuery } from './searchQuery'

export interface DecisionFilters {
  identifier?: string
  /** Which schemes to show. Several, because "every BMW scheme" is one
   *  question and four of them are BMW: asking one at a time means adding up
   *  four answers, which is four chances to add up the wrong ones (FIND-45). */
  schemes?: string[]
  /** One card, by the index this service stores rather than by its number.
   *
   *  It is here because the Attempts page produces the address: a row there is
   *  a pseudonym and following it has to land on that card's decisions. There
   *  is deliberately no box on the Decisions page for typing one in. Searching
   *  by pseudonym on demand is a different feature from following the row in
   *  front of you, and only the second one was asked for (OQ-32). */
  blindIndex?: string
  outcome?: string | null
  basis?: string | null
  authority?: string | null
  /** Who decided, which is not the lane. Theirs plus our own two, and a value
   *  this console does not know is still a valid filter: the vocabulary is
   *  Service OCPI's and may grow without a release here (FIND-43). */
  decidedBy?: string | null
  /** The authority's own word, matched exactly by the service. */
  reason?: string
  stationId?: string
  from?: string
  to?: string
  /** Canary authorizations alone (DEC-29). Off, the service sends none. */
  canary?: boolean
}

/** Every filter but the card, by the name the service reads. */
function filterValues(filters: DecisionFilters): Record<string, string | null | undefined> {
  return {
    identifier_blind_index: filters.blindIndex,
    scheme: filters.schemes?.length ? filters.schemes.join(',') : undefined,
    outcome: filters.outcome,
    basis: filters.basis,
    authority: filters.authority,
    decided_by: filters.decidedBy,
    reason: filters.reason,
    station_id: filters.stationId,
    // The operator's own zone goes on the range here, because the browser is
    // the only party that knows it. `asInstant` says what happens without it.
    from: asInstant(filters.from),
    to: asInstant(filters.to),
    canary: filters.canary ? 'true' : undefined,
  }
}

/** The filters as a URL query. Never the card: see `decisionsRequest`. */
export function toQuery(filters: DecisionFilters, cursor?: string | null): string {
  return searchQuery(filterValues(filters), cursor)
}

export type DecisionsRequest =
  | { method: 'get'; path: string }
  | { method: 'post'; path: '/decisions/search'; body: Record<string, string | boolean> }

/**
 * The request for a page of decisions.
 *
 * A CARD NEVER GOES IN A URL. The service's access log writes every URL, and
 * from there it reached Log Analytics in plain. With a card the whole search,
 * paging included, is a POST with everything in the body; without one it is
 * the GET it always was.
 */
export function decisionsRequest(
  filters: DecisionFilters,
  cursor?: string | null,
): DecisionsRequest {
  if (!filters.identifier) {
    return { method: 'get', path: `/decisions${toQuery(filters, cursor)}` }
  }
  const body: Record<string, string | boolean> = { identifier: filters.identifier }
  for (const [name, value] of Object.entries(filterValues(filters))) {
    // The index is the other way to name one card, and the card wins.
    if (value && name !== 'identifier_blind_index') body[name] = value
  }
  // A boolean in a body, as the contract has it, over the text a URL carries.
  if (filters.canary) body.canary = true
  if (cursor) body.cursor = cursor
  return { method: 'post', path: '/decisions/search', body }
}

/**
 * The request for the totals over exactly what the list shows (DEC-28 C1).
 *
 * The same filters, the same rule for a card: in the body of a POST, never in
 * a URL. No cursor, because the totals are of the whole filter, not a page.
 */
export function summaryRequest(filters: DecisionFilters): DecisionsRequest | SummaryPost {
  const asked = decisionsRequest(filters)
  if (asked.method === 'get') {
    return { method: 'get', path: asked.path.replace(/^\/decisions/, '/decisions/summary') }
  }
  return { method: 'post', path: '/decisions/summary', body: asked.body }
}

export type SummaryPost = {
  method: 'post'
  path: '/decisions/summary'
  body: Record<string, string | boolean>
}

/** Whether a decision arriving now belongs in the list as it is filtered. */
export function belongsInLiveList(
  filters: DecisionFilters,
  decision: DecisionSummary,
): boolean {
  // The stream carries no canary, and an ordinary arrival is not one.
  if (filters.canary) return false
  if (filters.blindIndex && decision.identifier_blind_index !== filters.blindIndex) {
    return false
  }
  if (filters.schemes?.length && !filters.schemes.includes(decision.scheme)) return false
  if (filters.outcome && decision.outcome !== filters.outcome) return false
  if (filters.basis && decision.basis !== filters.basis) return false
  if (filters.authority && decision.authority !== filters.authority) return false
  // The same rule as every line around it: a list means what its filter says.
  // Watching "decided by Service OCPI's own rule" must not fill with DCS
  // refusals arriving live, because nothing on screen would tell the reader
  // that the list stopped answering the question they asked.
  if (filters.decidedBy && decision.decided_by !== filters.decidedBy) return false
  if (filters.reason && decision.reason !== filters.reason) return false
  if (filters.stationId && decision.station_id !== filters.stationId) return false
  // A closed range cannot gain members. Letting one through would put a row
  // outside the window the filter names, on the page whose whole job is to
  // answer for that window.
  if (filters.to) return false
  return true
}

/**
 * Whether the live stream should run at all.
 *
 * Off for a closed range, because nothing arriving now is inside it, and a
 * "live" badge over a list that can never change reads as a service that has
 * stopped rather than as a filter that is doing its job. Off for canaries
 * too: the stream never carries one.
 */
export function liveIsMeaningful(filters: DecisionFilters): boolean {
  return !filters.to && !filters.canary
}
